import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC, FormEvent, ReactNode } from 'react'
import type { BlogPost } from '@/types'

import { useState } from 'react'
import Link from 'next/link'

import { Seo } from '@/components/generals/seo'
import { getAllPosts, getPostBySlug, getPostSlugs } from '@/data/blog'

interface BlogDetailProps {
    post: BlogPost
    related: BlogPost[]
}

const BlogDetail: FC<BlogDetailProps> = ({ post, related }): ReactNode => {
    const [submitted, setSubmitted] = useState(false)

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setSubmitted(true)
    }

    return (
        <>
            <Seo title={post.name} description={post.summary} ogImage={post.mainImage} />

            <div className='section project-details-section'>
                <div className='bg-holder'>
                    <img src='/images/Line-Image_1.svg' loading='lazy' alt='' className='bg-line-image extra-pct' />
                </div>
            </div>
            <div className='section'>
                <div className='black-background-color'>
                    <div className='container'>
                        <div className='w-layout-grid post-details-grid'>
                            <div id='w-node-d2c46902-380a-a1a6-5b66-bcb1769e3c3c-763bb78f' className='content'>
                                <div className='main-image-holder fade-in-move-on-scroll'>
                                    <img src={post.mainImage} loading='lazy' alt={post.name} className='main-image' />
                                </div>
                                <div id='w-node-e71331a3-cf72-ebf7-93d1-695e8ac2613b-763bb78f' className='rich-text-holder fade-in-move-on-scroll'>
                                    <h1 className='post-details-title'>{post.name}</h1>
                                    <div
                                        className='rich-text-details w-richtext'
                                        dangerouslySetInnerHTML={{ __html: post.body }}
                                    />
                                </div>
                            </div>
                            <div className='read-more-blogs-holder'>
                                <div className='read-more-blogs fade-in-move-on-scroll'>Read more blogs....</div>
                                <div className='read-more-blogs-wrapper w-dyn-list'>
                                    <div role='list' className='read-more-blogs-list w-dyn-items'>
                                        {related.map((item) => (
                                            <div key={item.slug} role='listitem' className='read-more-post-item w-dyn-item'>
                                                <Link href={`/blog/${item.slug}`} className='blog-details-link w-inline-block'>
                                                    <div className='blog-details-name'>{item.name}</div>
                                                    <p className='blog-details-read-more-item-summary'>{item.summary}</p>
                                                </Link>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className='email-subscribe-holder'>
                                    <div className='email-icon-holder'>
                                        <img src='/images/Line-Message-Gradient-Image.svg' loading='lazy' alt='' />
                                        <img src='/images/Message-Image.svg' loading='lazy' alt='' />
                                    </div>
                                    <div className='post-details-subscribe-title'>Subscribe to our news letter</div>
                                    <p>Mauris iaculis pretium augue at nunc nisi. Scelerisque cursus eu mattis volutpat, facilisis nisi quis.</p>
                                    <div className='subscribe-post-details-form'>
                                        <div className='form-block w-form'>
                                            {submitted ? (
                                                <div className='success-message w-form-done' style={{ display: 'block' }}>
                                                    <div>Thank you! Your submission has been received!</div>
                                                </div>
                                            ) : (
                                                <form name='Subscribe-Form' onSubmit={onSubmit} className='form-block-wrapp'>
                                                    <input className='email-form w-input' maxLength={256} name='Email' placeholder='Email' type='email' required />
                                                    <div className='button-arrow-holder'>
                                                        <input type='submit' className='submit-button w-button' value='Submit' />
                                                        <div className='button-gradient' />
                                                    </div>
                                                </form>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getPostSlugs()

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<BlogDetailProps> = async (context) => {
    const slug = context.params?.slug as string
    const post = await getPostBySlug(slug)

    if (!post) {
        return { notFound: true }
    }

    const related = (await getAllPosts()).filter((item) => item.slug !== slug)

    return { props: { post, related } }
}

export default BlogDetail
